/**
 * Dial code digits to ISO country. Lookup is exact equality on the full digit
 * string, never a prefix match: '569' must not resolve to Chile via '56'.
 *
 * '1' is deliberately absent — it covers both the US and Canada, so it cannot
 * identify a country. '1809' (Dominican Republic) is listed because production
 * stores it as '+1 809'.
 */
export const DIAL_CODE_TO_COUNTRY: Record<string, string> = {
  '51': 'PE',
  '56': 'CL',
  '54': 'AR',
  '55': 'BR',
  '52': 'MX',
  '598': 'UY',
  '57': 'CO',
  '595': 'PY',
  '593': 'EC',
  '591': 'BO',
  '502': 'GT',
  '506': 'CR',
  '503': 'SV',
  '809': 'DO',
  '1809': 'DO',
};

/**
 * The restaurant's country, from the dial code the widget has always used for
 * this. Comparing the stored string outright missed the venues held as a bare
 * '51', so the digits are what gets matched.
 */
export function resolveBillingCountry(countryCode?: string | null): string | null {
  return DIAL_CODE_TO_COUNTRY[(countryCode ?? '').replace(/\D/g, '')] ?? null;
}

/**
 * The venue's country: the dial code the widget has always resolved it from, and only when
 * that names nothing, the ISO code the admin stored. The dial code stays first so no venue's
 * fiscal receipt block or labels move; the backend resolves ISO first, and where the two
 * disagree (a data error) it drops a document type its country does not take to `Otro` and
 * reports it, which is the designed safety net, never a lost booking.
 */
export function resolveVenueCountry(isoCountryCode?: string | null, countryCode?: string | null): string | null {
  const byDialCode = resolveBillingCountry(countryCode);
  if (byDialCode !== null) return byDialCode;
  const iso = (isoCountryCode ?? '').trim();
  return /^[A-Za-z]{2}$/.test(iso) ? iso.toUpperCase() : null;
}
